import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ReactSelect from 'react-select';
import { Card, CardBody, Button, Typography } from '@material-tailwind/react';
import {
  FaChartBar,
  FaChartLine,
  FaFilePdf,
  FaEnvelope,
  FaFileCsv,
  FaCar,
  FaMoneyBillWave,
  FaBoxOpen,
  FaFilter,
  FaSyncAlt,
  FaWallet
} from 'react-icons/fa';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import usePermissions from '../../common/usePermissions';
import ReportLink from './shared/ReportLink';
import { downloadCsv, formatCurrency, formatDate, rangeLabels } from './shared/reportUtils';
import { downloadElementAsPdf } from './shared/reportExport';

const RANGE_OPTIONS = [
  { value: 'week',     label: 'Week' },
  { value: 'month',   label: 'Month' },
  { value: 'year',    label: 'Year' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom',  label: 'Custom Range' },
];

const REPORT_TABS = [
  { id: 'dashboard', label: 'Dashboard',             permission: 'reports.dashboard.view', icon: FaChartLine },
  { id: 'vehicle',   label: 'Vehicle Performance',   permission: 'reports.vehicle.view',   icon: FaCar },
  { id: 'financial', label: 'Financial Performance', permission: 'reports.finance.view',   icon: FaMoneyBillWave },
  { id: 'stock',     label: 'Stock Performance',     permission: 'reports.stock.view',     icon: FaBoxOpen },
];

const FINANCIAL_TABS = [
  { id: 'overview',           label: 'Overview',                      permission: 'reports.finance.overview.view' },
  { id: 'import-cashflow',    label: 'Import Cashflow',               permission: 'reports.finance.import_cashflow.view' },
  { id: 'cashflow-accounts',  label: 'Cashflow by Account',           permission: 'reports.finance.cashflow_accounts.view' },
  { id: 'mpesa',              label: 'M-Pesa Statement',              permission: 'reports.finance.mpesa.view' },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(255,87,51,0.15)' : 'none',
    '&:hover': { borderColor: '#FF5733' },
    fontSize: '0.875rem',
  }),
  menu: (base) => ({ ...base, zIndex: 50, borderRadius: '0.75rem', fontSize: '0.875rem' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#fff',
    color: '#1f2937',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af' }),
};

const EmptyState = ({ title, message }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
    <Typography className="text-lg font-semibold text-slate-700">{title}</Typography>
    <p className="mt-2 text-sm text-slate-500">{message}</p>
  </div>
);

const StatCard = ({ title, value, icon: Icon, tone = 'text-slate-900', hint }) => (
  <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
    <CardBody className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">{title}</p>
          <div className={`mt-1.5 text-lg font-black leading-tight break-words ${tone}`}>{value}</div>
          {hint ? <p className="mt-1 text-xs text-slate-500 truncate">{hint}</p> : null}
        </div>
        {Icon ? (
          <div className="flex-shrink-0 rounded-xl bg-[#fff4ef] p-2.5 text-[#FF5733]">
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
      </div>
    </CardBody>
  </Card>
);

const SectionCard = ({ title, children, actions = null }) => (
  <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
    <CardBody className="p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Typography className="text-base font-bold text-[#2a2e30]">{title}</Typography>
        {actions}
      </div>
      {children}
    </CardBody>
  </Card>
);

const ReportActions = ({ onPdf, onCsv, onEmail, pdfDisabled, csvDisabled, emailDisabled }) => (
  <div className="flex flex-wrap items-center gap-2">
    <button type="button" onClick={onPdf} disabled={pdfDisabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaFilePdf /> PDF
    </button>
    <button type="button" onClick={onCsv} disabled={csvDisabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaFileCsv /> CSV
    </button>
    <button type="button" onClick={onEmail} disabled={emailDisabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaEnvelope /> Send now
    </button>
  </div>
);

const EmailModal = ({ open, onClose, onSend, loading, defaultSubject, defaultMessage }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject || '');
  const [message, setMessage] = useState(defaultMessage || '');

  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject || '');
    setMessage(defaultMessage || '');
    setRecipientEmail('');
  }, [open, defaultMessage, defaultSubject]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <Typography className="text-xl font-bold text-[#2a2e30]">Send report now</Typography>
        <p className="mt-1 text-sm text-slate-500">Manual recipient entry only.</p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Recipient email</span>
            <input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" placeholder="name@example.com" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Message</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
          <button type="button" disabled={loading || !recipientEmail.trim()}
            onClick={() => onSend({ recipient_email: recipientEmail.trim(), subject: subject.trim(), message: message.trim() })}
            className="rounded-full bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TabBar = ({ tabs, activeTab, onChange }) => {
  const options = tabs.map((t) => ({ value: t.id, label: t.label }));
  return (
    <>
      {/* Desktop: button row */}
      <div className="hidden sm:flex flex-wrap items-center gap-0 border-b border-slate-200 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => onChange(tab.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition ${
                active ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
              {Icon ? <Icon className="h-4 w-4" /> : null}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      {/* Mobile: react-select */}
      <div className="sm:hidden">
        <ReactSelect
          value={options.find((o) => o.value === activeTab) || null}
          onChange={(opt) => opt && onChange(opt.value)}
          options={options}
          isSearchable={false}
          isClearable={false}
          styles={selectStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
        />
      </div>
    </>
  );
};

const SubTabBar = ({ tabs, activeTab, onChange }) => {
  const options = tabs.map((t) => ({ value: t.id, label: t.label }));
  return (
    <>
      <div className="hidden sm:flex flex-wrap items-center gap-0 border-b border-slate-100 overflow-x-auto">
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => onChange(tab.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-2 text-xs font-semibold transition ${
                active ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="sm:hidden">
        <ReactSelect
          value={options.find((o) => o.value === activeTab) || null}
          onChange={(opt) => opt && onChange(opt.value)}
          options={options}
          isSearchable={false}
          isClearable={false}
          styles={selectStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
        />
      </div>
    </>
  );
};

const ReportsHub = () => {
  const { token } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const contentRef = useRef(null);

  const LS = {
    get: (key, fallback) => { try { const v = localStorage.getItem(`reports_hub_${key}`); return v !== null ? v : fallback; } catch { return fallback; } },
    set: (key, value)    => { try { localStorage.setItem(`reports_hub_${key}`, value ?? ''); } catch {} },
    del: (key)           => { try { localStorage.removeItem(`reports_hub_${key}`); } catch {} },
  };

  const [activeTab, setActiveTab]               = useState(() => LS.get('activeTab', 'dashboard'));
  const [activeFinancialTab, setActiveFinancialTab] = useState(() => LS.get('activeFinancialTab', 'overview'));
  const [draftRange, setDraftRange]             = useState(() => LS.get('draftRange', 'week'));
  const [selectedWeekKey, setSelectedWeekKey]   = useState(() => LS.get('selectedWeekKey', null) || null);
  const [selectedMonth, setSelectedMonth]       = useState(() => LS.get('selectedMonth', ''));
  const [selectedYear, setSelectedYear]         = useState(() => LS.get('selectedYear', ''));
  const [dateFrom, setDateFrom]                 = useState(() => LS.get('dateFrom', ''));
  const [dateTo, setDateTo]                     = useState(() => LS.get('dateTo', ''));
  const [weeks, setWeeks]                       = useState([]);
  const [weeksLoading, setWeeksLoading]         = useState(false);
  const [appliedRange, setAppliedRange]         = useState(() => LS.get('draftRange', 'week'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('activeTab', activeTab); },           [activeTab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('activeFinancialTab', activeFinancialTab); }, [activeFinancialTab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('draftRange', draftRange); },         [draftRange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('selectedWeekKey', selectedWeekKey); }, [selectedWeekKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('selectedMonth', selectedMonth); },   [selectedMonth]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('selectedYear', selectedYear); },     [selectedYear]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('dateFrom', dateFrom); },             [dateFrom]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { LS.set('dateTo', dateTo); },                 [dateTo]);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState('');
  const [report, setReport]                     = useState(null);
  const [emailOpen, setEmailOpen]               = useState(false);
  const [emailLoading, setEmailLoading]         = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token || localStorage.getItem('authToken') || ''}`
  }), [token]);

  // Fetch week list when user picks the week range
  useEffect(() => {
    if (draftRange !== 'week' || weeks.length > 0) return;
    setWeeksLoading(true);
    axios.get(`${rootUrl}/weekly-summary/weeks`, { headers: authHeaders })
      .then((res) => {
        const data = res.data || [];
        setWeeks(data);
        if (!selectedWeekKey && data.length > 0) setSelectedWeekKey(String(data[0].week));
      })
      .catch(() => {})
      .finally(() => setWeeksLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRange, rootUrl, authHeaders, weeks.length, selectedWeekKey]);

  const weekOptions = useMemo(() => weeks.map((w) => {
    const start = new Date(w.start_date).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });
    const end   = new Date(w.end_date  ).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' });
    return { value: String(w.week), label: `Week ${w.week} — ${start} to ${end}` };
  }), [weeks]);

  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let y = now.getFullYear(); y >= 2020; y--) {
      const maxM = y === now.getFullYear() ? now.getMonth() + 1 : 12;
      for (let m = maxM; m >= 1; m--) {
        const val = `${y}-${String(m).padStart(2, '0')}`;
        opts.push({ value: val, label: new Date(y, m - 1, 1).toLocaleDateString('en-KE', { month: 'long', year: 'numeric' }) });
      }
    }
    return opts;
  }, []);

  const yearOptions = useMemo(() => {
    const opts = [];
    for (let y = new Date().getFullYear(); y >= 2020; y--) {
      opts.push({ value: String(y), label: String(y) });
    }
    return opts;
  }, []);

  const availableTabs          = useMemo(() => REPORT_TABS.filter((t) => hasPermission(t.permission)), [hasPermission]);
  const availableFinancialTabs = useMemo(() => FINANCIAL_TABS.filter((t) => hasPermission(t.permission)), [hasPermission]);

  useEffect(() => {
    if (availableTabs.length && !availableTabs.some((t) => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  useEffect(() => {
    if (availableFinancialTabs.length && !availableFinancialTabs.some((t) => t.id === activeFinancialTab)) {
      setActiveFinancialTab(availableFinancialTabs[0].id);
    }
  }, [availableFinancialTabs, activeFinancialTab]);

  const activeLabel = useMemo(() => {
    if (activeTab === 'financial') {
      return availableFinancialTabs.find((t) => t.id === activeFinancialTab)?.label || 'Financial Performance';
    }
    return availableTabs.find((t) => t.id === activeTab)?.label || 'Reports';
  }, [activeFinancialTab, activeTab, availableFinancialTabs, availableTabs]);

  const activeEndpoint = useMemo(() => {
    if (activeTab === 'dashboard') return '/api/reports/dashboard';
    if (activeTab === 'vehicle')   return '/api/reports/vehicle-performance';
    if (activeTab === 'stock')     return '/api/reports/stock-performance';
    if (activeTab === 'financial') {
      if (activeFinancialTab === 'import-cashflow')   return '/api/reports/financial-performance/import-cashflow';
      if (activeFinancialTab === 'cashflow-accounts') return '/api/reports/financial-performance/cashflow-accounts';
      if (activeFinancialTab === 'mpesa')             return '/api/reports/financial-performance/mpesa';
      return '/api/reports/financial-performance/overview';
    }
    return '/api/reports/dashboard';
  }, [activeFinancialTab, activeTab]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { range: draftRange };
      if (draftRange === 'week'   && selectedWeekKey) params.week_key = selectedWeekKey;
      if (draftRange === 'month'  && selectedMonth)   params.month    = selectedMonth;
      if (draftRange === 'year'   && selectedYear)    params.year     = selectedYear;
      if (draftRange === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
      }

      const response = await axios.get(`${rootUrl}${activeEndpoint}`, { headers: authHeaders, params });
      setReport(response.data?.data || null);
      setAppliedRange(draftRange);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!permissionsLoading && availableTabs.length > 0 && activeTab) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeFinancialTab, availableTabs.length, permissionsLoading]);

  const onApply = () => fetchReport();

  const onExportPdf = async () => {
    if (!contentRef.current) return;
    await downloadElementAsPdf(contentRef.current, {
      filename: `reports-${activeLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${appliedRange}.pdf`,
      reportTitle: 'Reports',
      sectionTitle: activeLabel,
      subtitle: rangeLabels[appliedRange] || appliedRange
    });
  };

  const buildCsvForActiveReport = () => {
    if (!report) return null;
    if (activeTab === 'dashboard') {
      return {
        headers: ['Metric', 'Value'],
        rows: Object.entries(report.summary_cards || {}).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : v]),
        filename: 'reports-dashboard.csv'
      };
    }
    if (activeTab === 'vehicle') {
      return {
        headers: ['Plate No', 'Owner', 'Driver', 'Status', 'Money In', 'Money Out', 'Net', 'Transactions', 'Last Activity'],
        rows: (report.rows || []).map((r) => [r.plate_no, r.owner_name, r.driver_name, r.status_name, r.money_in, r.money_out, r.net_cashflow, r.transaction_count, r.last_activity]),
        filename: 'reports-vehicle-performance.csv'
      };
    }
    if (activeTab === 'financial') {
      if (activeFinancialTab === 'cashflow-accounts') {
        return {
          headers: ['Account', 'Type', 'Money In', 'Money Out', 'Net', 'Balance', 'Entries'],
          rows: (report.rows || []).map((r) => [r.account_name, r.account_type, r.money_in, r.money_out, r.net_cashflow, r.current_balance, r.entry_count]),
          filename: 'reports-cashflow-accounts.csv'
        };
      }
      if (activeFinancialTab === 'mpesa') {
        return {
          headers: ['Week', 'Account', 'Rows', 'Money In', 'Money Out', 'Posted', 'Pending'],
          rows: (report.rows || []).map((r) => [r.week_key, r.mpesa_account, r.row_count, r.money_in, r.money_out, r.posted_rows, r.pending_rows]),
          filename: 'reports-mpesa-statement.csv'
        };
      }
      return {
        headers: ['Week', 'Money In', 'Money Out', 'Bank In', 'Bank Out', 'Entries'],
        rows: (report.overview || []).map((r) => [r.week_key, r.money_in, r.money_out, r.bank_in, r.bank_out, r.entry_count]),
        filename: 'reports-financial-overview.csv'
      };
    }
    return {
      headers: ['Part', 'Category', 'Quantity', 'Unit Cost', 'Stock Value', 'Selling Value', 'Transactions'],
      rows: (report.rows || []).map((r) => [r.part_name, r.category, r.quantity, r.unit_cost, r.stock_value, r.selling_value, r.transaction_count]),
      filename: 'reports-stock-performance.csv'
    };
  };

  const onExportCsv = () => {
    const csv = buildCsvForActiveReport();
    if (csv) downloadCsv(csv.headers, csv.rows, csv.filename);
  };

  const onSendNow = async ({ recipient_email, subject, message }) => {
    setEmailLoading(true);
    try {
      await axios.post(`${rootUrl}/api/reports/send-now`, {
        report_type: activeTab, report_title: activeLabel,
        recipient_email, subject, message,
        summary: activeTab === 'dashboard' ? report?.summary_cards : undefined
      }, { headers: authHeaders });
      setEmailOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send report email');
    } finally {
      setEmailLoading(false);
    }
  };

  const dashboardTrend      = Array.isArray(report?.trend)             ? report.trend             : [];
  const dashboardTopVehicles = Array.isArray(report?.top_vehicles)      ? report.top_vehicles      : [];
  const financialRows        = Array.isArray(report?.overview)          ? report.overview          : [];
  const accountRows          = Array.isArray(report?.cashbook_accounts) ? report.cashbook_accounts : [];
  const mpesaRows            = Array.isArray(report?.mpesa_statement)   ? report.mpesa_statement   : [];
  const vehicleRows          = Array.isArray(report?.rows)              ? report.rows              : [];
  const stockRows            = Array.isArray(report?.rows)              ? report.rows              : [];
  const deadStockRows        = Array.isArray(report?.dead_stock)        ? report.dead_stock        : [];
  const fastMovingRows       = Array.isArray(report?.fast_moving)       ? report.fast_moving       : [];

  const canPdf   = hasPermission('reports.export.pdf');
  const canCsv   = hasPermission('reports.export.csv');
  const canEmail = hasPermission('reports.email');

  const isCustom = draftRange === 'custom';
  const canApply =
    (draftRange === 'week'     && !!selectedWeekKey) ||
    (draftRange === 'month'    && !!selectedMonth)   ||
    (draftRange === 'year'     && !!selectedYear)    ||
    (draftRange === 'all_time')                       ||
    (draftRange === 'custom'   && (!!dateFrom || !!dateTo));

  if (!availableTabs.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
        <EmptyState title="Reports unavailable" message="You do not have access to the Reports hub yet." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6">

        {/* Header */}
        <div className="mb-5">
          <Typography className="text-3xl font-black text-[#2a2e30]">Reports</Typography>
          <p className="mt-1 text-sm text-slate-500">
            Fleet, cashflow, stock, and finance summaries with drilldown detail pages.
          </p>
        </div>

        {/* Main tabs */}
        <div className="mb-1">
          <TabBar tabs={availableTabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>

        {/* Financial sub-tabs */}
        {activeTab === 'financial' && availableFinancialTabs.length > 0 ? (
          <div className="mb-1">
            <SubTabBar tabs={availableFinancialTabs} activeTab={activeFinancialTab} onChange={setActiveFinancialTab} />
          </div>
        ) : null}

        {/* Filter bar */}
        <Card className="my-4 border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
          <CardBody className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Label */}
              <div className="flex items-center gap-2 self-end pb-2">
                <FaFilter className="text-[#FF5733] flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-700">Filters</span>
              </div>

              {/* Range type */}
              <div className="w-36 flex-shrink-0">
                <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">Range</label>
                <ReactSelect
                  value={RANGE_OPTIONS.find((o) => o.value === draftRange) || null}
                  onChange={(opt) => {
                    if (!opt) return;
                    setDraftRange(opt.value);
                    setDateFrom(''); setDateTo('');
                    setSelectedWeekKey(null); setSelectedMonth(''); setSelectedYear('');
                  }}
                  options={RANGE_OPTIONS}
                  isSearchable={false}
                  isClearable={false}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              {/* Week picker */}
              {draftRange === 'week' ? (
                <div className="min-w-[200px] flex-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">Week</label>
                  <ReactSelect
                    value={weekOptions.find((o) => o.value === selectedWeekKey) || null}
                    onChange={(opt) => opt && setSelectedWeekKey(opt.value)}
                    options={weekOptions}
                    isLoading={weeksLoading}
                    placeholder="Select week…"
                    isClearable={false}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              ) : null}

              {/* Month picker */}
              {draftRange === 'month' ? (
                <div className="min-w-[180px] flex-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">Month</label>
                  <ReactSelect
                    value={monthOptions.find((o) => o.value === selectedMonth) || null}
                    onChange={(opt) => opt && setSelectedMonth(opt.value)}
                    options={monthOptions}
                    placeholder="Select month…"
                    isClearable={false}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              ) : null}

              {/* Year picker */}
              {draftRange === 'year' ? (
                <div className="w-28 flex-shrink-0">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">Year</label>
                  <ReactSelect
                    value={yearOptions.find((o) => o.value === selectedYear) || null}
                    onChange={(opt) => opt && setSelectedYear(opt.value)}
                    options={yearOptions}
                    placeholder="Select year…"
                    isClearable={false}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              ) : null}

              {/* Custom date range */}
              {isCustom ? (
                <div className="w-36 flex-shrink-0">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">From</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF5733]" />
                </div>
              ) : null}
              {isCustom ? (
                <div className="w-36 flex-shrink-0">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wide">To</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF5733]" />
                </div>
              ) : null}

              {/* Apply + export actions — inline with the filters */}
              <div className="flex flex-wrap items-center gap-2 self-end pb-0.5">
                <button type="button" onClick={onApply} disabled={loading || !canApply}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#FF5733] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e64c22] disabled:cursor-not-allowed disabled:opacity-50">
                  <FaSyncAlt className={loading ? 'animate-spin' : ''} />
                  {loading ? 'Loading…' : 'Apply'}
                </button>
                <ReportActions
                  onPdf={onExportPdf} onCsv={onExportCsv} onEmail={() => setEmailOpen(true)}
                  pdfDisabled={!canPdf || loading} csvDisabled={!canCsv || loading} emailDisabled={!canEmail || loading}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {error ? (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        ) : null}

        <div ref={contentRef} className="space-y-5">
          {loading ? (
            <div className="rounded-2xl border border-[#eadfd8] bg-white p-10 text-center shadow-sm">
              <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-[#FF5733]" />
              <p className="text-sm text-slate-600">Loading {activeLabel.toLowerCase()}…</p>
            </div>
          ) : null}

          {/* ── DASHBOARD ── */}
          {!loading && activeTab === 'dashboard' ? (
            <>
              {/* Job Cards / WAR / WAI stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-1">
                <StatCard
                  title="Job Cards Invoiced"
                  value={formatCurrency(report?.summary_cards?.jc_invoiced_amount)}
                  hint={`${report?.summary_cards?.jc_invoiced_count ?? 0} card${report?.summary_cards?.jc_invoiced_count !== 1 ? 's' : ''}`}
                  icon={FaChartBar}
                  tone="text-[#FF5733]"
                />
                <StatCard
                  title="WAR Remittance"
                  value={formatCurrency(report?.summary_cards?.war)}
                  hint="Weekly Amount Remittance"
                  icon={FaMoneyBillWave}
                  tone="text-sky-600"
                />
                <StatCard
                  title="WAI Invoice"
                  value={formatCurrency(report?.summary_cards?.wai)}
                  hint="Weekly Amount Invoice"
                  icon={FaWallet}
                  tone="text-emerald-600"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <StatCard title="Cushion Net"        value={formatCurrency(report?.summary_cards?.cushion_net)}                                                    icon={FaMoneyBillWave} tone="text-rose-600"    hint="CUS minus CUR" />
                <StatCard title="Total Expenses"     value={formatCurrency(report?.summary_cards?.total_expenses)}                                                  icon={FaMoneyBillWave} tone="text-amber-600"   hint="All expenses for period" />
                <StatCard title="Net Cashflow"       value={formatCurrency(report?.summary_cards?.net_cashflow)}                                                     icon={FaChartBar}      tone="text-[#FF5733]" />
                <StatCard title="Vehicle Utilization" value={`${report?.summary_cards?.vehicle_utilization || 0}%`}                                                 icon={FaCar}           tone="text-sky-600" />
                <StatCard title="Stock Value"        value={formatCurrency(report?.summary_cards?.stock_value)}                                                      icon={FaBoxOpen}       tone="text-violet-600" />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <SectionCard title="Weekly Trend">
                  {dashboardTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={dashboardTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" tickFormatter={(v) => formatDate(v)} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(v) => formatDate(v)} />
                        <Line type="monotone" dataKey="money_in"  stroke="#10B981" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="money_out" stroke="#EF4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <EmptyState title="No trend data" message="No activity for the selected period." />}
                </SectionCard>

                <SectionCard title="Vehicle Mix">
                  {dashboardTopVehicles.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={dashboardTopVehicles.slice(0, 8)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="plate_no" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="net_cashflow" fill="#FF5733" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState title="No vehicle data" message="Vehicle performance appears once transactions exist." />}
                </SectionCard>
              </div>

              <SectionCard title="Top Performing Vehicles">
                {dashboardTopVehicles.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Vehicle</th>
                          <th className="px-3 py-2">Owner</th>
                          <th className="hidden md:table-cell px-3 py-2">Driver</th>
                          <th className="px-3 py-2 text-right">Money In</th>
                          <th className="px-3 py-2 text-right">Money Out</th>
                          <th className="px-3 py-2 text-right">Net</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardTopVehicles.map((row) => (
                          <tr key={row.plate_no} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-3 py-3 font-semibold text-slate-700">{row.plate_no}</td>
                            <td className="px-3 py-3 text-slate-600">{row.owner_name || row.owner_code || '-'}</td>
                            <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.driver_name || '-'}</td>
                            <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.money_in)}</td>
                            <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.money_out)}</td>
                            <td className="px-3 py-3 text-right font-semibold text-slate-900">{formatCurrency(row.net_cashflow)}</td>
                            <td className="px-3 py-3">
                              <ReportLink to={`/reports/drilldowns/vehicle/${encodeURIComponent(row.plate_no)}`} newTab>Open</ReportLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <EmptyState title="No vehicles" message="Open transactions first to populate vehicle summaries." />}
              </SectionCard>
            </>
          ) : null}

          {/* ── VEHICLE PERFORMANCE ── */}
          {!loading && activeTab === 'vehicle' ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard title="Vehicles"   value={vehicleRows.length}                                                                                        icon={FaCar}           tone="text-slate-900" />
                <StatCard title="Money In"   value={formatCurrency(vehicleRows.reduce((s, r) => s + Number(r.money_in   || 0), 0))}                            icon={FaMoneyBillWave} tone="text-emerald-600" />
                <StatCard title="Money Out"  value={formatCurrency(vehicleRows.reduce((s, r) => s + Number(r.money_out  || 0), 0))}                            icon={FaMoneyBillWave} tone="text-amber-600" />
                <StatCard title="Net"        value={formatCurrency(vehicleRows.reduce((s, r) => s + Number(r.net_cashflow || 0), 0))}                          icon={FaChartBar}      tone="text-[#FF5733]" />
              </div>

              <SectionCard title="Vehicle Performance" actions={<span className="text-xs text-slate-500">Drilldown opens in new tab</span>}>
                {vehicleRows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Vehicle</th>
                          <th className="px-3 py-2">Owner</th>
                          <th className="hidden md:table-cell px-3 py-2">Driver</th>
                          <th className="hidden md:table-cell px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Income</th>
                          <th className="px-3 py-2 text-right">Expense</th>
                          <th className="px-3 py-2 text-right">Net</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicleRows.map((row) => (
                          <tr key={row.plate_no} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-3 py-3 font-semibold text-slate-700">{row.plate_no}</td>
                            <td className="px-3 py-3 text-slate-600">{row.owner_name || row.owner_code || '-'}</td>
                            <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.driver_name || '-'}</td>
                            <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.status_name || '-'}</td>
                            <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.money_in)}</td>
                            <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.money_out)}</td>
                            <td className="px-3 py-3 text-right font-semibold text-slate-900">{formatCurrency(row.net_cashflow)}</td>
                            <td className="px-3 py-3">
                              <ReportLink to={`/reports/drilldowns/vehicle/${encodeURIComponent(row.plate_no)}`} newTab>Open</ReportLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <EmptyState title="No vehicle rows" message="No vehicles with activity for this period." />}
              </SectionCard>
            </>
          ) : null}

          {/* ── FINANCIAL PERFORMANCE ── */}
          {!loading && activeTab === 'financial' ? (
            <>
              {activeFinancialTab === 'overview' || activeFinancialTab === 'import-cashflow' ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard title="Weeks"     value={financialRows.length}                                                                                  icon={FaChartLine}     tone="text-slate-900" />
                    <StatCard title="Money In"  value={formatCurrency(financialRows.reduce((s, r) => s + Number(r.money_in  || 0), 0))}                        icon={FaMoneyBillWave} tone="text-emerald-600" />
                    <StatCard title="Money Out" value={formatCurrency(financialRows.reduce((s, r) => s + Number(r.money_out || 0), 0))}                        icon={FaMoneyBillWave} tone="text-amber-600" />
                    <StatCard title="Banked"    value={formatCurrency(financialRows.reduce((s, r) => s + Number(r.bank_in   || 0), 0))}                        icon={FaWallet}        tone="text-sky-600" />
                  </div>
                  <SectionCard title={activeFinancialTab === 'import-cashflow' ? 'Import Cashflow' : 'Import Cashflow Overview'}>
                    {financialRows.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                              <th className="px-3 py-2">System Week</th>
                              <th className="px-3 py-2 text-right">Money In</th>
                              <th className="px-3 py-2 text-right">Money Out</th>
                              <th className="hidden md:table-cell px-3 py-2 text-right">Bank In</th>
                              <th className="hidden md:table-cell px-3 py-2 text-right">Bank Out</th>
                              <th className="px-3 py-2 text-right">Entries</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financialRows.map((row) => (
                              <tr key={row.week_key} className="border-b border-slate-100 hover:bg-slate-50/50">
                                <td className="px-3 py-3 font-semibold text-slate-700">{row.week_key}</td>
                                <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.money_in)}</td>
                                <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.money_out)}</td>
                                <td className="hidden md:table-cell px-3 py-3 text-right text-sky-600">{formatCurrency(row.bank_in)}</td>
                                <td className="hidden md:table-cell px-3 py-3 text-right text-amber-600">{formatCurrency(row.bank_out)}</td>
                                <td className="px-3 py-3 text-right text-slate-700">{row.entry_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <EmptyState title="No cashflow rows" message="Select a different period and click Apply." />}
                  </SectionCard>
                </>
              ) : null}

              {activeFinancialTab === 'cashflow-accounts' ? (
                <SectionCard title="Cashflow by Cashbook Account">
                  {accountRows.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Account</th>
                            <th className="hidden md:table-cell px-3 py-2">Type</th>
                            <th className="px-3 py-2 text-right">Money In</th>
                            <th className="px-3 py-2 text-right">Money Out</th>
                            <th className="px-3 py-2 text-right">Net</th>
                            <th className="hidden md:table-cell px-3 py-2 text-right">Balance</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountRows.map((row) => (
                            <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                              <td className="px-3 py-3 font-semibold text-slate-700">{row.account_name}</td>
                              <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.account_type}</td>
                              <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.money_in)}</td>
                              <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.money_out)}</td>
                              <td className="px-3 py-3 text-right font-semibold text-slate-900">{formatCurrency(row.net_cashflow)}</td>
                              <td className="hidden md:table-cell px-3 py-3 text-right text-slate-700">{formatCurrency(row.current_balance)}</td>
                              <td className="px-3 py-3">
                                <ReportLink to={`/reports/drilldowns/finance-account/${encodeURIComponent(row.id)}`} newTab>Open</ReportLink>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState title="No cashbook accounts" message="Open a date range with cashbook activity first." />}
                </SectionCard>
              ) : null}

              {activeFinancialTab === 'mpesa' ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard title="Total Rows"  value={mpesaRows.reduce((s, r) => s + Number(r.row_count    || 0), 0)} icon={FaWallet} tone="text-slate-900" />
                    <StatCard title="Money In"    value={formatCurrency(mpesaRows.reduce((s, r) => s + Number(r.money_in  || 0), 0))} icon={FaMoneyBillWave} tone="text-emerald-600" />
                    <StatCard title="Money Out"   value={formatCurrency(mpesaRows.reduce((s, r) => s + Number(r.money_out || 0), 0))} icon={FaMoneyBillWave} tone="text-amber-600" />
                    <StatCard title="Pending Rows" value={mpesaRows.reduce((s, r) => s + Number(r.pending_rows || 0), 0)} icon={FaWallet} tone="text-rose-600" />
                  </div>
                  <SectionCard title="M-Pesa Statement Performance">
                    {mpesaRows.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                              <th className="px-3 py-2">System Week</th>
                              <th className="px-3 py-2">Account</th>
                              <th className="px-3 py-2 text-right">Rows</th>
                              <th className="px-3 py-2 text-right">Money In</th>
                              <th className="px-3 py-2 text-right">Money Out</th>
                              <th className="px-3 py-2 text-right">Posted</th>
                              <th className="px-3 py-2 text-right">Pending</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mpesaRows.map((row) => (
                              <tr key={`${row.week_key}-${row.mpesa_account}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                                <td className="px-3 py-3 font-semibold text-slate-700">{row.week_key}</td>
                                <td className="px-3 py-3 text-slate-600">{row.mpesa_account}</td>
                                <td className="px-3 py-3 text-right text-slate-700">{row.row_count}</td>
                                <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.money_in)}</td>
                                <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.money_out)}</td>
                                <td className="px-3 py-3 text-right text-slate-700">{row.posted_rows}</td>
                                <td className={`px-3 py-3 text-right font-semibold ${row.pending_rows > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{row.pending_rows}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <EmptyState title="No M-Pesa rows" message="Select a different period and apply again." />}
                  </SectionCard>
                </>
              ) : null}
            </>
          ) : null}

          {/* ── STOCK PERFORMANCE ── */}
          {!loading && activeTab === 'stock' ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard title="Items"         value={report?.summary?.item_count     || 0}                            icon={FaBoxOpen} tone="text-slate-900" />
                <StatCard title="Total Quantity" value={report?.summary?.total_quantity || 0}                           icon={FaBoxOpen} tone="text-sky-600" />
                <StatCard title="Low Stock"     value={report?.summary?.low_stock_count || 0}                           icon={FaBoxOpen} tone="text-amber-600" />
                <StatCard title="Stock Value"   value={formatCurrency(report?.summary?.stock_value)}                    icon={FaBoxOpen} tone="text-[#FF5733]" />
              </div>

              <SectionCard title="Stock Performance">
                {stockRows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Part</th>
                          <th className="hidden md:table-cell px-3 py-2">Category</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="hidden md:table-cell px-3 py-2 text-right">Unit Cost</th>
                          <th className="px-3 py-2 text-right">Stock Value</th>
                          <th className="hidden md:table-cell px-3 py-2 text-right">Transactions</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockRows.map((row) => (
                          <tr key={row.part_id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${row.is_below_min_quantity ? 'bg-amber-50/60' : ''}`}>
                            <td className="px-3 py-3 font-semibold text-slate-700">
                              <div className="flex flex-col gap-1">
                                <span>{row.part_name}</span>
                                <div className="flex flex-wrap gap-1">
                                  {row.is_stale_pricing ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Stale pricing</span> : null}
                                  {row.is_below_min_quantity ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Below min</span> : null}
                                  {row.is_above_max_quantity ? <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">Above max</span> : null}
                                </div>
                              </div>
                            </td>
                            <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.category || '-'}</td>
                            <td className="px-3 py-3 text-right text-slate-700">{row.quantity}</td>
                            <td className="hidden md:table-cell px-3 py-3 text-right text-slate-700">{formatCurrency(row.unit_cost)}</td>
                            <td className="px-3 py-3 text-right text-slate-700">{formatCurrency(row.stock_value)}</td>
                            <td className="hidden md:table-cell px-3 py-3 text-right text-slate-700">{row.transaction_count}</td>
                            <td className="px-3 py-3">
                              <ReportLink to={`/reports/drilldowns/stock-item/${encodeURIComponent(row.part_id)}`} newTab>Open</ReportLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <EmptyState title="No stock rows" message="Stock performance will appear once items exist." />}
              </SectionCard>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Dead Stock">
                  {deadStockRows.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Part</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-right">Stock Value</th>
                            <th className="px-3 py-2 text-right">Days Idle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deadStockRows.map((row) => (
                            <tr key={row.part_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                              <td className="px-3 py-3 font-semibold text-slate-700">{row.part_name}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{row.quantity}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{formatCurrency(row.stock_value)}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{row.days_without_movement}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState title="No dead stock" message="No stocked parts are idle in this period." />}
                </SectionCard>

                <SectionCard title="Fast Moving Parts">
                  {fastMovingRows.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Part</th>
                            <th className="px-3 py-2 text-right">Outbound Qty</th>
                            <th className="px-3 py-2 text-right">Moves</th>
                            <th className="px-3 py-2 text-right">Total Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fastMovingRows.map((row) => (
                            <tr key={row.part_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                              <td className="px-3 py-3 font-semibold text-slate-700">{row.part_name}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{row.outbound_quantity}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{row.movement_count}</td>
                              <td className="px-3 py-3 text-right text-slate-700">{formatCurrency(row.total_cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState title="No fast movers" message="Outbound movement will appear once parts move in this period." />}
                </SectionCard>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <EmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={onSendNow}
        loading={emailLoading}
        defaultSubject={`Reports: ${activeLabel}`}
        defaultMessage={`Summary for ${activeLabel.toLowerCase()} (${rangeLabels[appliedRange] || appliedRange}).`}
      />
    </div>
  );
};

export default ReportsHub;
